import React, { useEffect, useState } from 'react';
import { DxItemGroup, DxItemGroupItem, LoadingPlaceholder, CodeFence, DataTable, DataTableRow, AlertBlock } from 'genesys-react-components';
import { addToast, ToastType } from '../../../helpers/atoms/ToastAtom';
import CmsApi from '../../../helpers/CmsApi';

const methods: { [key: string]: number } = {
	GET: 0,
	POST: 1,
	PUT: 2,
	PATCH: 3,
	DELETE: 4,
};

export interface Client {
	id: string;
	name: string;
}

interface UsageItem {
	httpMethod: string;
	templateUri: string;
	requests: number;
	requiredScopes: string[];
}

export interface UsageResponse {
	usedScopes?: string[];
	usageResults?: UsageItem[];
	totalOrgs: Number;
}

export default function BuiltInClientUsage() {
	const [loading, setLoading] = useState(false);
	const [scopes, setScopes] = useState<string>('');
	const [usageRows, setUsageRows] = useState<DataTableRow[]>();
	const [orgCount, setOrgCount] = useState<Number>(0);
	const [error, setError] = useState<string | null>(null);
	const [clients, setClients] = useState<DxItemGroupItem[]>([]);

	useEffect(() => {
		function loadClients() {
			CmsApi.getbuiltInClients()
				.then((data) => {
					const clientItems: DxItemGroupItem[] = (data || [])
						.sort((a: Client, b: Client) => {
							if (a.name.toLowerCase() < b.name.toLowerCase()) {
								return -1;
							}
							if (a.name.toLowerCase() > b.name.toLowerCase()) {
								return 1;
							}
							return 0;
						})
						.map((client: Client) => {
							return {
								label: client.name,
								value: client.id,
							};
						});
					clientItems.unshift({ label: '--- Select a Built-In Client ---', value: '', disabled: true, isSelected: true });
					setClients(clientItems);
				})
				.catch((err) => {
					setError(err.toString());
					addToast({ title: 'Unable to get OAuth clients', message: err.message, toastType: ToastType.Critical });
				});
		}
		loadClients();
	}, []);

	function onClientChanged(item: DxItemGroupItem) {
		if (item.value) {
			loadClientUsage(item.value);
		}
	}

	function loadClientUsage(clientId: string) {
		setLoading(true);
		setScopes('');
		setUsageRows(undefined);
		setError(null);
		CmsApi.getbuiltInClientUsage(clientId)
			.then((data) => {
				if (data?.usedScopes && data.usedScopes.length) {
					setScopes(
						data.usedScopes.sort().reduce((result: string, scope: string) => {
							return `${result}  - ${scope}\n`;
						}, 'scopes:\n')
					);
				}
				setOrgCount(data?.totalOrgs || 0);
				if (data?.usageResults && data.usageResults.length) {
					const rows: DataTableRow[] = data.usageResults
						.sort((a: UsageItem, b: UsageItem) => {
							if (a.templateUri < b.templateUri) {
								return -1;
							}
							if (a.templateUri > b.templateUri) {
								return 1;
							}
							const methodA = methods[a.httpMethod] ?? 999;
							const methodB = methods[b.httpMethod] ?? 999;
							if (methodA < methodB) {
								return -1;
							}
							if (methodA > methodB) {
								return 1;
							}
							return a.requests - b.requests;
						})
						.map((item: UsageItem) => {
							return {
								cells: [
									{ content: item.httpMethod },
									{ content: item.templateUri },
									{ content: item.requests },
									{ content: item.requiredScopes?.join(', ') || '' },
								],
							} as DataTableRow;
						});
					setUsageRows(rows);
				} else {
					setUsageRows([]);
				}
			})
			.catch((err) => {
				setError(err.toString());
				addToast({ title: 'Unable to get client usage data', message: err.message, toastType: ToastType.Critical });
			})
			.finally(() => {
				setLoading(false);
			});
	}

	return (
		<div>
			<DxItemGroup title="Built-In OAuth Client" format="dropdown" items={clients} disabled={loading} onItemChanged={onClientChanged} />
			{loading && <LoadingPlaceholder text="Loading API usage.." />}
			{usageRows !== undefined && usageRows.length !== 0 && (
				<>
					<h3>Scopes Used</h3>
					<p>
						Add this output to the <code>scopes</code> section of{' '}
						<a href="https://bitbucket.org/inindca/auth-api/src/master/builtin_clients.d/" target="_blank" rel="noopener noreferrer">
							auth-api/builtin_clients.d/
						</a>
						.
					</p>
					{scopes.length !== 0 ? <CodeFence value={scopes} /> : <div>No scopes found</div>}
					<h3>Org Count</h3>
					<p>
						Total orgs using client: <strong>{`${orgCount}`}</strong>
					</p>
					<h3>API Usage</h3>
					{usageRows && !loading && (
						<DataTable
							filterable={true}
							sortable={true}
							headerRow={{
								cells: [{ content: 'HTTP Method' }, { content: 'URL Template' }, { content: 'Requests' }, { content: 'Required Scopes' }],
							}}
							rows={usageRows}
						/>
					)}
				</>
			)}
			{usageRows?.length === 0 && <AlertBlock alertType="info">No usage found</AlertBlock>}
			{error !== null && <AlertBlock alertType="critical">{'An unexpected error occured. Please refresh the page'}</AlertBlock>}
		</div>
	);
}
